// client/src/components/tendermaker/ProfileVaultPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
  tmProfileList,
  tmProfileInitUpload,
  tmProfileCommit,
  tmProfileMetaGet,
  tmProfileMetaSave,
} from "./tendermakerApi";
import Layout from "../Layout";

const MASTER = [
  { id: "pan", label: "PAN Card" },
  { id: "gst", label: "GST Registration Certificate" },
  { id: "udyam", label: "Udyam / MSME Certificate" },
  { id: "itr", label: "ITR (Last FY)" },
  { id: "turnover_ca", label: "Turnover Certificate from CA" },
  { id: "bank_statement", label: "Bank Statement / Solvency (if available)" },
  { id: "undertaking_nd", label: "Non-Disclosure Undertaking (Template)" },
  { id: "declaration_no_blacklist", label: "Declaration - Non Blacklisting (Template)" },

  // ✅ Bid Studio assets
  { id: "letterhead_header", label: "Letterhead Header (PNG/JPG recommended)" },
  { id: "letterhead_footer", label: "Letterhead Footer (PNG/JPG recommended)" },

  { id: "signature", label: "Signature (PNG recommended)" },
  { id: "stamp", label: "Company Stamp (PNG recommended)" },
];

const box = { border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "white" };
const btn = { padding: "10px 14px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fafafa", cursor: "pointer", fontWeight: 800 };

export default function ProfileVaultPage() {
  const [items, setItems] = useState([]);
  const [masterId, setMasterId] = useState(MASTER[0].id);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // ✅ Profile Meta fields (exact names from your backend)
  const [companyName, setCompanyName] = useState("");
  const [signingName, setSigningName] = useState("");
  const [signingDesignation, setSigningDesignation] = useState("");
  const [metaLoaded, setMetaLoaded] = useState(false);

  async function refresh() {
    const r = await tmProfileList();
    setItems(r.data || []);
  }

  async function loadMeta() {
    const r = await tmProfileMetaGet();
    const d = r?.data || null;

    setCompanyName(d?.companyName || "");
    setSigningName(d?.signingName || "");
    setSigningDesignation(d?.signingDesignation || "");
    setMetaLoaded(true);
  }

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([refresh(), loadMeta()]);
      } catch (e) {
        setErr(String(e?.message || e));
      }
    })();
  }, []);

  async function upload(file) {
    setBusy(true);
    setErr("");
    try {
      const m = MASTER.find((x) => x.id === masterId);

      const init = await tmProfileInitUpload({
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        masterId,
        label: m?.label || null,
      });

      const put = await fetch(init.putUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!put.ok) throw new Error(`PUT failed: ${put.status}`);

      await tmProfileCommit({
        key: init.key,
        fileName: file.name,
        contentType: file.type || null,
        masterId,
        label: m?.label || null,
      });

      await refresh();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function saveMeta() {
    setBusy(true);
    setErr("");
    try {
      await tmProfileMetaSave({ companyName, signingName, signingDesignation });
      await loadMeta();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  // show status for assets (best-effort)
  const latestByMaster = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const k = it.masterId;
      if (!k) continue;
      if (!map.has(k)) map.set(k, it); // assumes backend list newest-first
    }
    return map;
  }, [items]);

  const lhH = latestByMaster.get("letterhead_header");
  const lhF = latestByMaster.get("letterhead_footer");

  const sig = latestByMaster.get("signature");
  const stp = latestByMaster.get("stamp");

  return (
    <Layout>
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 16 }}>
      <h2 style={{ marginBottom: 6 }}>TenderMaker — Profile Vault</h2>
      <div style={{ color: "#6b7280", marginBottom: 16 }}>
        Upload your company’s standard documents once. These will be used to prepare bid submissions.
      </div>

      {err ? <div style={{ color: "crimson", marginBottom: 12 }}>{err}</div> : null}

      {/* ✅ Signing Meta */}
      <div style={{ ...box, marginBottom: 16 }}>
        <div style={{ fontWeight: 900, marginBottom: 6 }}>Profile Meta — Signing Authority</div>
        <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 10 }}>
          Used for undertakings and for applying signature/stamp in Bid Studio.
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Company Name"
            disabled={busy}
            style={{ padding: 10, borderRadius: 10, border: "1px solid #e5e7eb" }}
          />

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              value={signingName}
              onChange={(e) => setSigningName(e.target.value)}
              placeholder="Signing Name"
              disabled={busy}
              style={{ padding: 10, borderRadius: 10, border: "1px solid #e5e7eb", flex: 1, minWidth: 240 }}
            />
            <input
              value={signingDesignation}
              onChange={(e) => setSigningDesignation(e.target.value)}
              placeholder="Signing Designation"
              disabled={busy}
              style={{ padding: 10, borderRadius: 10, border: "1px solid #e5e7eb", flex: 1, minWidth: 240 }}
            />
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={saveMeta} style={btn} disabled={busy}>
              Save Meta
            </button>

            {metaLoaded ? (
              <div style={{ color: "#6b7280", fontSize: 12 }}>
                Current: <b style={{ color: "#111827" }}>{signingName || "-"}</b> •{" "}
                <b style={{ color: "#111827" }}>{signingDesignation || "-"}</b> •{" "}
                <b style={{ color: "#111827" }}>{companyName || "-"}</b>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* ✅ asset quick status */}
      <div style={{ ...box, marginBottom: 16, background: "#fafafa" }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Bid Studio Assets (quick status)</div>
        <div style={{ color: "#6b7280", fontSize: 13 }}>
          Header: <b style={{ color: lhH ? "#111827" : "crimson" }}>{lhH ? "uploaded" : "missing"}</b>
          {"  "}• Footer: <b style={{ color: lhF ? "#111827" : "crimson" }}>{lhF ? "uploaded" : "missing"}</b>

          {"  "}• Signature: <b style={{ color: sig ? "#111827" : "crimson" }}>{sig ? "uploaded" : "missing"}</b>
          {"  "}• Stamp: <b style={{ color: stp ? "#111827" : "crimson" }}>{stp ? "uploaded" : "missing"}</b>
        </div>
        <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
          Recommendation: Signature + Stamp as PNG with transparent background.
        </div>
      </div>

      {/* upload */}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Upload Profile Document</div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={masterId}
            onChange={(e) => setMasterId(e.target.value)}
            disabled={busy}
            style={{ padding: 10, borderRadius: 10, border: "1px solid #e5e7eb", minWidth: 360 }}
          >
            {MASTER.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label} ({m.id})
              </option>
            ))}
          </select>

          <input
            type="file"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload(f);
            }}
          />
        </div>

        {busy ? <div style={{ marginTop: 8 }}>Uploading / Saving…</div> : null}
      </div>

      {/* list */}
      <div style={{ fontWeight: 900, marginBottom: 8 }}>Your uploaded profile docs</div>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
        {items.map((d) => (
          <div key={d.id} style={{ padding: 12, borderTop: "1px solid #f3f4f6" }}>
            <div style={{ fontWeight: 700 }}>{d.label || d.masterId}</div>
            <div style={{ color: "#6b7280", fontSize: 13 }}>
              masterId: {d.masterId} • file: {d.fileName || d.key}
            </div>
          </div>
        ))}
        {items.length === 0 ? <div style={{ padding: 12, color: "#6b7280" }}>No profile docs uploaded yet.</div> : null}
      </div>
    </div>
    </Layout>
  );
}
